import React, { useEffect, useRef, useState } from 'react';
import {
  Button,
  Dropdown,
  Input,
  InputRef,
  message,
  Modal,
  Space,
  Switch,
  TableColumnType,
  TableProps,
  Tooltip,
} from 'antd';
import {
  CopyOutlined,
  DeleteOutlined,
  EllipsisOutlined,
  LinkOutlined,
  MenuOutlined,
  QuestionCircleOutlined,
  SearchOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import Highlighter from 'react-highlight-words';
import type { FilterDropdownProps } from 'antd/es/table/interface';
import { Link, useRequest } from 'umi';
import CampForm, { productList } from '../components/campForm';
import {
  BatchInfoType,
  deleteCardCamp,
  listAll,
  modifyRecommendable,
  modifyVisible,
  sortCamp,
  TCardCampVO,
} from '@/services/CardCampRequest';
import styles from './index.less';
import { TFinishRule } from '@/types/camp/campIndex/indexTypes';
import { get, isUndefined } from 'lodash';
import DragList from '@/pages/backend/cardCampSettings/nodeSettings/DragList';
import useIsHaveCreateCampAuth from '@/pages/backend/cardCamp/useHaveCreateCampAuth';
import { copyText } from '@/utils';

export type PromotionalImagesObj = {
  index: number;
  name: string;
  mobileUrl?: string;
  pcUrl?: string;
  mode: string;
  checked?: boolean;
};

export type RecruitPageType = {
  recruitPage: {
    dynamicCommunication: {
      dynamicCommunication: boolean;
      index: number;
      mode: string;
    };
    promotionalImages: PromotionalImagesObj[];
  };
};

export interface CardCampType {
  isHaveNode: boolean;
  id: string;
  name: string;
  product: string;
  ownerId: number;
  ownerName: string;
  visible: number;
  recommendable: number;
  batchInfo: BatchInfoType;
  finishRule?: TFinishRule;
}

type DataIndex = keyof CardCampType;

const convertProduct = (value: string): string => {
  if (!value) {
    return '';
  }
  // product 是多选，后端存逗号拼接串，需逐段转换
  return (
    value
      .split(',')
      .map((item) => productList.find((product) => product.value === item)?.label || item)
      .join(',') || ''
  );
};

function transformCamps(camps: TCardCampVO[]): CardCampType[] {
  return (
    camps?.map((camp) => ({
      id: camp.id,
      name: camp.name,
      product: camp.product,
      ownerId: camp.ownerId,
      ownerName: camp.ownerName,
      visible: camp.visible,
      recommendable: camp.recommendable,
      isHaveNode: camp.isHaveNode,
      batchInfo: {
        beforeStartNum: camp.batchInfo?.beforeStartNum || 0,
        startNum: camp.batchInfo?.startNum || 0,
        endNum: camp.batchInfo?.endNum || 0,
      },
    })) || []
  );
}

function CardCampPage() {
  const [copyCampId, setCopyCampId] = useState<string | undefined>();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [camps, setCamps] = useState<CardCampType[]>([]);
  const [searchText, setSearchText] = useState('');
  const [searchedColumn, setSearchedColumn] = useState('');
  const searchInput = useRef<InputRef>(null);

  const handleSearch = (
    selectedKeys: string[],
    confirm: FilterDropdownProps['confirm'],
    dataIndex: DataIndex,
  ) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters: () => void) => {
    clearFilters();
    setSearchText('');
  };

  const getColumnSearchProps = (dataIndex: DataIndex): TableColumnType<CardCampType> => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
          style={{ marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            搜索
          </Button>
          <Button
            onClick={() => {
              clearFilters && handleReset(clearFilters);
              handleSearch(selectedKeys as string[], confirm, dataIndex);
            }}
            size="small"
            style={{ width: 90 }}
          >
            重置
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
    ),
    onFilter: (value, record) =>
      (record[dataIndex] as any)
        .toString()
        .toLowerCase()
        .includes((value as string).toLowerCase()),
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ''}
        />
      ) : (
        text
      ),
  });

  const showModal = () => {
    setIsModalOpen(true);
  };

  const handleOk = () => {
    setIsModalOpen(false);
    if (copyCampId) {
      setCopyCampId(undefined);
    }
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    if (copyCampId) {
      setCopyCampId(undefined);
    }
  };

  const columns: TableProps<CardCampType>['columns'] = [
    {
      title: ' ',
      key: 'sort',
      dataIndex: 'sort',
      width: 30,
      render: () => <MenuOutlined style={{ cursor: 'grab', color: '#999' }} />,
    },
    {
      title: '打卡营名称',
      dataIndex: 'name',
      key: 'name',
      ...getColumnSearchProps('name'),
      width: 250,
      render: (text, record) => (
        <div className="truncate" style={{ maxWidth: 230 }}>
          <Link to={`/v2/console/campsetings/${record.id}`}>{text}</Link>
        </div>
      ),
    },
    {
      title: '产品',
      dataIndex: 'product',
      key: 'product',
      width: 120,
      filters: productList.map((item) => ({ text: item.label, value: item.value })),
      onFilter: (value, record) => record.product.includes(value as string),
      render: (text) => (
        <div className="truncate" style={{ maxWidth: 100 }}>
          {convertProduct(text)}
        </div>
      ),
    },
    {
      title: '所有者',
      dataIndex: 'ownerName',
      key: 'ownerName',
    },
    {
      title: '批次信息',
      key: 'batchInfo',
      dataIndex: 'batchInfo',
      render: (_, { batchInfo }) => (
        <>
          <Space size="middle">
            <span>
              未开始：<span className="text-yellow">{batchInfo.beforeStartNum}</span>
            </span>
            <span>
              进行中：<span className="text-green">{batchInfo.startNum}</span>
            </span>
            <span>
              已结束：<span className="text-8c8c8c">{batchInfo.endNum}</span>
            </span>
          </Space>
        </>
      ),
    },
    {
      title: '是否展示',
      dataIndex: 'visible',
      key: 'visible',
      render: (value, record) => {
        if (!record.isHaveNode) {
          return (
            <Tooltip title="没有设置打卡节点，不能设置是否展示打卡营">
              <Switch disabled />
            </Tooltip>
          );
        }
        return (
          <>
            <Switch
              checked={record.visible === 1}
              onChange={() => visibleChangeRun(record.id, record.visible === 1 ? 0 : 1)}
            />
          </>
        );
      },
    },
    {
      title: (
        <Space>
          首页推荐
          <Tooltip title="开启后将在学院首页按照顺序展示推荐位">
            <QuestionCircleOutlined />
          </Tooltip>
        </Space>
      ),
      dataIndex: 'recommendable',
      key: 'recommendable',
      render: (value, record) => {
        const recommendableList = camps.filter((item) => item.recommendable === 1);
        const dom = (
          <Switch
            disabled={!record.isHaveNode}
            checked={record.recommendable === 1}
            onChange={(checked) => {
              if (checked && record.visible !== 1) {
                // 首页推荐依赖是否展示，联动默认开启
                recommendableChangeRun(record.id, 1, 1);
              } else {
                recommendableChangeRun(record.id, checked ? 1 : 0);
              }
            }}
          />
        );
        if (!record.isHaveNode) {
          return <Tooltip title="没有设置打卡节点，不能设置首页推荐">{dom}</Tooltip>;
        }
        if (!record.visible) {
          return <Tooltip title="开启首页推荐后将自动开启是否展示">{dom}</Tooltip>;
        }
        return dom;
      },
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <>
          <Dropdown menu={{ items: items(record.id, record) }}>
            <EllipsisOutlined />
          </Dropdown>
        </>
      ),
    },
  ];

  const { run: listCampsRun, loading } = useRequest(
    (sort?: boolean) => {
      return listAll();
    },
    {
      manual: true,
      onSuccess: async (data, params) => {
        const tempCamps = transformCamps(data as TCardCampVO[]);
        setCamps(tempCamps);
        if (params[0]) {
          const oldCamps = tempCamps.map((item, index) => ({ id: item.id, index: index + 1 }));
          //const newCamps = [{ id, index: 0 }];
          await sortCamp({
            items: oldCamps,
          });
        }
      },
      onError: (e) => {
        message.error(e.message);
      },
    },
  );

  const { run: visibleChangeRun } = useRequest(
    (id, visible) => {
      return modifyVisible(id, visible);
    },
    {
      manual: true,
      onSuccess: (_, params) => {
        const updatedCamps = camps?.map((e) => {
          if (e.id === params[0]) {
            return { ...e, visible: params[1] };
          }
          return e;
        });
        setCamps(updatedCamps);
      },
      onError: (e) => {
        message.error(e.message);
      },
    },
  );

  const { run: recommendableChangeRun } = useRequest(
    async (id, recommendable, visible?: number) => {
      if (!isUndefined(visible)) {
        await modifyVisible(id, visible);
      }
      return modifyRecommendable(id, recommendable);
    },
    {
      manual: true,
      onSuccess: (_, params) => {
        const [id, recommendable, visible] = params;
        setCamps((prev) =>
          prev.map((e) => {
            if (e.id === id) {
              return { ...e, recommendable, visible: isUndefined(visible) ? e.visible : visible };
            }
            return e;
          }),
        );
      },
      onError: (e) => {
        message.error(e.message);
      },
    },
  );

  useEffect(() => {
    listCampsRun();
  }, []);

  const haveAuth = useIsHaveCreateCampAuth();

  const items = (id: string, record: CardCampType) => {
    const baseItemArray = [
      {
        key: '1',
        label: (
          <Link to={`/v2/console/campsetings/${id}`}>
            <SettingOutlined style={{ marginRight: 8 }} />
            打卡营管理
          </Link>
        ),
        children: [
          {
            key: '11',
            label: <Link to={`/v2/console/campsetings/${id}?tab=basic`}>基本设置</Link>,
          },
          {
            key: '12',
            label: <Link to={`/v2/console/campsetings/${id}?tab=form`}>报名表单设置</Link>,
          },
          {
            key: '13',
            label: <Link to={`/v2/console/campsetings/${id}?tab=admissions`}>招生页设置</Link>,
          },
          {
            key: '14',
            label: <Link to={`/v2/console/campsetings/${id}?tab=node`}>打卡节点设置</Link>,
          },
          {
            key: '15',
            label: <Link to={`/v2/console/campsetings/${id}?tab=reward`}>奖励设置</Link>,
          },
          {
            key: '16',
            label: <Link to={`/v2/console/campsetings/${id}?tab=rules`}>结营设置</Link>,
          },
          {
            key: '17',
            label: <Link to={`/v2/console/campsetings/${id}?tab=class`}>批次设置</Link>,
          },
        ],
      },
      {
        key: '2',
        label: (
          <span
            onClick={() => {
              copyText(`${location.origin}/v2/camp/${id}`, () => {
                message.success('招生页链接已经复制到剪贴板');
              });
              //window.open(`/v2/camp/${id}`);
            }}
          >
            <LinkOutlined style={{ marginRight: 8 }} />
            招生链接
          </span>
        ),
      },
      {
        type: 'divider',
      },
      {
        key: '3',
        label: (
          <span
            onClick={async () => {
              Modal.confirm({
                cancelButtonProps: {
                  type: 'primary',
                  danger: true,
                },
                okButtonProps: {
                  type: 'default',
                },
                title: `确认删除该打卡营？`,
                content:
                  '打卡营删除后无法恢复，且所有参与过该打卡营的用户将无法再看到此打卡营，请谨慎操作。',
                okText: '取消',
                cancelText: '删除',
                onCancel: async () => {
                  if (
                    get(record, 'batchInfo.beforeStartNum') ||
                    get(record, 'batchInfo.startNum')
                  ) {
                    Modal.info({
                      title: '该打卡营有未结束的批次，无法删除',
                      okType: 'primary',
                      okText: '我知道了',
                    });
                  } else {
                    await deleteCardCamp(id);
                    await listCampsRun();
                  }
                },
              });
            }}
          >
            <DeleteOutlined style={{ marginRight: 8 }} />
            删除
          </span>
        ),
      },
    ];
    if (haveAuth) {
      const findIdx = baseItemArray.findIndex((item) => item.key === '2');
      if (findIdx > -1) {
        baseItemArray.splice(findIdx, 0, {
          key: 'copy',
          label: (
            <div
              onClick={() => {
                setCopyCampId(record.id);
                setIsModalOpen(true);
              }}
            >
              <CopyOutlined style={{ marginRight: 8 }} />
              复制打卡营
            </div>
          ),
        });
      }
    }
    return baseItemArray;
  };

  return (
    <div className={styles.card_camp_list}>
      {haveAuth && (
        <Button style={{ marginBottom: '16px' }} type="primary" onClick={showModal}>
          新建打卡营
        </Button>
      )}
      <Modal
        width={1000}
        title={copyCampId ? '复制打卡营' : '新建打卡营'}
        open={isModalOpen}
        onOk={handleOk}
        onCancel={handleCancel}
        footer={null}
        centered
        destroyOnClose
      >
        <CampForm
          isModal
          isCopyMode={!isUndefined(copyCampId)}
          index={0}
          onCancel={handleCancel}
          id={copyCampId}
          // index={camps.length}
          onOk={async (id) => {
            handleCancel();
            if (id) {
              await listCampsRun(true);
            }
          }}
        />
      </Modal>
      <DragList<CardCampType>
        onDragEnd={async (data) => {
          await sortCamp({ items: data.map((dataItem, index) => ({ id: dataItem.id, index })) });
        }}
        loading={loading}
        data={camps}
        tableProps={{ columns }}
      />
      {/*<Table columns={columns} dataSource={camps} pagination={false} />*/}
    </div>
  );
}

export default CardCampPage;
